import { PDFDocument, PDFPage, PDFFont, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { OfferForm } from "../types/OfferObject";

// ─── Layout constants ──────────────────────────────────────────────────────────
const MARGIN_X = 72;      // ~1 inch left/right – standard legal doc margin
const MARGIN_TOP = 72;    // ~1 inch top
const MARGIN_BOTTOM = 72; // ~1 inch bottom
const LINE_INDENT = 20;   // clause / bullet indent

const FS = {
  title: 14,
  sectionHead: 11,
  body: 11,
  small: 9,
};

// leading = space between baselines
const LEAD = {
  title: 20,
  sectionHead: 16,
  body: 17,   // relaxed body leading – closest to MS Word "normal" spacing
  small: 13,
};

/**
 * Generates the full Droomwork Independent Contractor Services Agreement
 * as a polished PDF from scratch, with all dynamic values substituted inline.
 *
 * @param offer       - The offer form data (dates, milestones, payment type…)
 * @param clientName  - Full name / company name of the client (hiring party)
 * @param contractorName - Full name of the independent contractor (talent)
 * @param currency    - Currency symbol, defaults to ₦
 */
export async function generateDroomworkContract(params: {
  offer: OfferForm;
  clientName: string;
  contractorName: string;
  currency?: string;
}): Promise<File> {
  const { offer, clientName, contractorName, currency = "₦" } = params;

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);

  const [fontByte, fontByteBold] = await Promise.all([
    fetch("/fonts/Inter_18pt-Regular.ttf").then((r) => r.arrayBuffer()),
    fetch("/fonts/Inter_18pt-Bold.ttf").then((r) => r.arrayBuffer()),
  ]);

  const regular = await pdfDoc.embedFont(fontByte);
  const bold = await pdfDoc.embedFont(fontByteBold);

  // ── computed end date ──────────────────────────────────────────────────────
  const computedEndDate = (() => {
    const start = new Date(offer.startDate);
    const days = parseInt(offer.agreed_duration ?? "0", 10);
    if (!isNaN(start.getTime()) && !isNaN(days)) {
      const end = new Date(start);
      end.setDate(end.getDate() + days);
      return end.toISOString().split("T")[0];
    }
    return "N/A";
  })();

  // ── page / cursor state ────────────────────────────────────────────────────
  let pg: PDFPage = pdfDoc.addPage();
  const pgW = pg.getSize().width;
  const pgH = pg.getSize().height;
  const contentW = pgW - MARGIN_X * 2;
  let y = pgH - MARGIN_TOP;

  // ── helpers ────────────────────────────────────────────────────────────────

  /** Split text into lines that fit within maxW at the given font + size. */
  function wrapText(
    text: string,
    f: PDFFont,
    size: number,
    maxW: number
  ): string[] {
    // Handle newlines in source text
    const paragraphs = text.split("\n");
    const result: string[] = [];

    for (const para of paragraphs) {
      if (para.trim() === "") {
        result.push("");
        continue;
      }
      const words = para.split(" ");
      let line = "";
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (f.widthOfTextAtSize(test, size) > maxW && line) {
          result.push(line);
          line = word;
        } else {
          line = test;
        }
      }
      if (line) result.push(line);
    }
    return result;
  }

  /** Add a new page when the remaining vertical space is insufficient. */
  function ensureSpace(needed: number) {
    if (y - needed < MARGIN_BOTTOM) {
      pg = pdfDoc.addPage();
      y = pgH - MARGIN_TOP;
    }
  }

  /**
   * Draw wrapped text at current y, advancing y for each line.
   * `indent` shifts the text from the left margin.
   */
  function draw(
    text: string,
    opts: {
      size?: number;
      f?: PDFFont;
      indent?: number;
      center?: boolean;
      lead?: number;
    } = {}
  ) {
    const size = opts.size ?? FS.body;
    const font = opts.f ?? regular;
    const indent = opts.indent ?? 0;
    const lead = opts.lead ?? LEAD.body;
    const maxW = contentW - indent;
    const lines = wrapText(text, font, size, maxW);

    for (const line of lines) {
      ensureSpace(lead);
      const xBase = MARGIN_X + indent;
      const x = opts.center
        ? (pgW - font.widthOfTextAtSize(line, size)) / 2
        : xBase;
      if (line !== "") {
        pg.drawText(line, { x, y, size, font, color: rgb(0, 0, 0) });
      }
      y -= lead;
    }
  }

  /** Insert vertical whitespace (gap between paragraphs / sections). */
  function gap(h: number) {
    y -= h;
    if (y < MARGIN_BOTTOM) {
      pg = pdfDoc.addPage();
      y = pgH - MARGIN_TOP;
    }
  }

  /** A numbered section heading with a blank line before it. */
  function section(num: string, title: string) {
    gap(18);
    draw(`${num}. ${title}`, {
      size: FS.sectionHead,
      f: bold,
      lead: LEAD.sectionHead,
    });
    gap(6);
  }

  /** A numbered sub-section heading. */
  function sub(label: string) {
    gap(10);
    draw(label, { f: bold, lead: LEAD.body });
    gap(4);
  }

  /** A bullet-indented paragraph. */
  function bullet(text: string) {
    const bulletSymbol = "\u2022  ";
    draw(`${bulletSymbol}${text}`, { indent: LINE_INDENT });
    gap(4);
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  TITLE
  // ════════════════════════════════════════════════════════════════════════════
  draw("INDEPENDENT CONTRACTOR SERVICES AGREEMENT (NIGERIA)", {
    size: FS.title,
    f: bold,
    center: true,
    lead: LEAD.title,
  });
  gap(14);

  draw(
    "IMPORTANT DISCLAIMER: This template is provided for informational purposes only. " +
      "Droomwork is not providing legal advice. Users must confirm that the terms of this " +
      "agreement are suitable for their project and comply with the laws of the " +
      "Federal Republic of Nigeria.",
    { size: FS.small, lead: LEAD.small }
  );
  gap(18);

  // ════════════════════════════════════════════════════════════════════════════
  //  PARTIES
  // ════════════════════════════════════════════════════════════════════════════
  draw(
    `THIS SERVICES AGREEMENT ("Agreement") is entered into as of ${offer.startDate} ` +
      `(the "Effective Date") between:`
  );
  gap(10);

  draw(`CLIENT: ${clientName || "[Client Name]"}, Nigeria ("Client"),`, { f: bold });
  gap(8);
  draw("AND");
  gap(8);
  draw(
    `INDEPENDENT: ${contractorName || "[Contractor Name]"}, Nigeria ("Independent").`,
    { f: bold }
  );
  gap(8);
  draw(`Client and Independent are each a "Party" and collectively the "Parties."`);
  gap(18);

  // ════════════════════════════════════════════════════════════════════════════
  //  RECITALS
  // ════════════════════════════════════════════════════════════════════════════
  draw("RECITALS", { size: FS.sectionHead, f: bold, lead: LEAD.sectionHead });
  gap(8);
  draw(
    "The Parties agree to utilize the platform provided by Droomwork to facilitate the Project. " +
      "The Parties agree that this Agreement does not change their obligations to follow " +
      "Droomwork's Terms of Service."
  );
  gap(18);

  // ════════════════════════════════════════════════════════════════════════════
  //  1. SCOPE OF SERVICES
  // ════════════════════════════════════════════════════════════════════════════
  section("1", "SCOPE OF SERVICES");
  draw(
    `Independent agrees to perform the services and provide the deliverables (the "Deliverables") ` +
      `described in the Project Scope on the Droomwork platform. Independent affirms they possess ` +
      `the requisite skills and experience to perform these Services.`
  );
  gap(10);
  draw("Project Scope:", { f: bold });
  gap(4);
  draw(offer.description || "As specified on the Droomwork platform.", {
    indent: LINE_INDENT,
  });

  // ════════════════════════════════════════════════════════════════════════════
  //  2. PROJECT TIMELINE & ACCEPTANCE
  // ════════════════════════════════════════════════════════════════════════════
  section("2", "PROJECT TIMELINE & ACCEPTANCE");

  sub("2.1 Term.");
  draw(
    "The Project starts on the date specified in the Droomwork platform or upon Client's upfront " +
      "payment. It ends upon completion of Services or termination by either Party."
  );

  sub("2.2 Project Start and End Dates");
  gap(4);
  draw(
    `(a) The Project Start Date shall be ${offer.startDate} or, if the Project includes an ` +
      `upfront payment, the date the Client makes the upfront payment, whichever date is later.`,
    { indent: LINE_INDENT }
  );
  gap(6);
  draw(`(b) The Project End Date shall be ${computedEndDate}.`, {
    indent: LINE_INDENT,
  });

  sub("2.3 Acceptance of Deliverables.");
  bullet(
    "(a) Automatic Approval: IF CLIENT DOES NOT REQUEST REVISIONS OR SUBMIT A DISPUTE " +
      "VIA DROOMWORK WITHIN 120 HOURS OF DELIVERY, THE DELIVERABLES ARE DEEMED APPROVED " +
      "AND PAYMENT WILL BE AUTOMATICALLY RELEASED."
  );
  bullet(
    '(b) Revisions: If Client requests revisions via a "Defect Report" on Droomwork within ' +
      "the 120-hour window, Independent shall modify and resubmit the Deliverables within a " +
      "reasonable time. This cycle repeats until approval or a Dispute is logged."
  );

  sub("2.4 Delivery Schedule / Milestone Payments");
  draw(
    `If applicable, list dates for when the Deliverables, designs, modules, software, products, ` +
      `documentation and other materials will be provided to Client ("Milestone Deliverable") ` +
      `and, if Milestone Payments apply, the corresponding payment ("Milestone Payment") for ` +
      `each Milestone Deliverable.`
  );
  gap(12);

  // ── Milestone table ──────────────────────────────────────────────────────
  if (offer.milestones?.length > 0) {
    const col1W = contentW * 0.46;
    const col2W = contentW * 0.28;
    const col3Offset = col1W + col2W;
    const rowLead = LEAD.body;

    ensureSpace(60);

    // Header
    pg.drawText("Milestone Deliverable", {
      x: MARGIN_X,
      y,
      size: FS.body,
      font: bold,
      color: rgb(0, 0, 0),
    });
    pg.drawText("Due Date", {
      x: MARGIN_X + col1W,
      y,
      size: FS.body,
      font: bold,
      color: rgb(0, 0, 0),
    });
    pg.drawText("Payment", {
      x: MARGIN_X + col3Offset,
      y,
      size: FS.body,
      font: bold,
      color: rgb(0, 0, 0),
    });
    y -= rowLead;

    // Divider
    pg.drawLine({
      start: { x: MARGIN_X, y: y + 4 },
      end: { x: MARGIN_X + contentW, y: y + 4 },
      thickness: 0.75,
      color: rgb(0, 0, 0),
    });
    y -= 10;

    for (const ms of offer.milestones) {
      const descLines = wrapText(ms.description, regular, FS.body, col1W - 8);
      const rowH = descLines.length * rowLead + 8;
      ensureSpace(rowH);

      const rowStartY = y;
      descLines.forEach((line, i) => {
        pg.drawText(line, {
          x: MARGIN_X,
          y: rowStartY - i * rowLead,
          size: FS.body,
          font: regular,
          color: rgb(0, 0, 0),
        });
      });

      const dueDate = ms.endDate ? ms.endDate.split("T")[0] : "N/A";
      pg.drawText(dueDate, {
        x: MARGIN_X + col1W,
        y: rowStartY,
        size: FS.body,
        font: regular,
        color: rgb(0, 0, 0),
      });
      pg.drawText(`${currency}${ms.amountPayable}`, {
        x: MARGIN_X + col3Offset,
        y: rowStartY,
        size: FS.body,
        font: regular,
        color: rgb(0, 0, 0),
      });

      y -= rowH;
    }
    gap(10);
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  3. COMPENSATION & PAYMENT
  // ════════════════════════════════════════════════════════════════════════════
  section("3", "COMPENSATION & PAYMENT");

  sub("3.1 Fees.");
  draw(
    "Client shall pay Independent based on the method selected on the Droomwork platform " +
      "(Fixed Fee, Hourly, or Milestones)."
  );
  gap(8);

  const isFixed =
    offer.work_type === "fixedcost" && offer.payment_type !== "milestone-based";
  const isMilestone = offer.payment_type === "milestone-based";
  const isRetainer = offer.work_type === "retainer";

  draw(
    `(a) ${isFixed ? "[X]" : "[ ]"} One-time fixed amount. Flat fee of ` +
      `${currency}${offer.agreed_total_cost} released to Independent upon completion of the ` +
      `Project. Client agrees to pay Independent ${currency}0.00 as a nonrefundable, upfront ` +
      `payment before Independent begins any work. The remaining balance is withheld from the ` +
      `Independent's account wallet until Client accepts Deliverables or does not accept nor ` +
      `request revisions within one hundred twenty (120) hours after Independent provides ` +
      `Deliverables to Client.`,
    { indent: LINE_INDENT }
  );
  gap(8);

  draw(
    `(b) ${isMilestone ? "[X]" : "[ ]"} Milestone deliverables. Payments will be made ` +
      `according to the Milestone Delivery Schedule in Section 2.4. Client will deposit the ` +
      `amount of the first Milestone Deliverable and will deposit subsequent Milestone payments ` +
      `after acceptance of the preceding Milestone Deliverable. Client will have one hundred ` +
      `twenty (120) hours to accept each Milestone Deliverable. If Client does not accept or ` +
      `request revisions within one hundred twenty (120) hours, the amount will be automatically ` +
      `released into Independent's Droomwork account wallet.`,
    { indent: LINE_INDENT }
  );

  if (isRetainer) {
    gap(8);
    draw(
      `(c) [X] Retainer. Payments are made on a ` +
        `${offer.retainer_agreed_interval ?? "periodic"} basis. ` +
        `Interval payment: ${currency}${offer.retainer_agreed_interval_payment ?? "N/A"}.`,
      { indent: LINE_INDENT }
    );
  }

  sub("3.2 Payment Processing.");
  draw(
    "Payments are processed via Droomwork's third-party processor. " +
      "Client is responsible for processing fees."
  );

  sub("3.3 Taxes.");
  draw("Tax obligations are governed by Addendum A (Section 2) attached hereto.");

  // ════════════════════════════════════════════════════════════════════════════
  //  4. RELATIONSHIP OF THE PARTIES
  // ════════════════════════════════════════════════════════════════════════════
  section("4", "RELATIONSHIP OF THE PARTIES");

  sub("4.1 Independent Contractor Status.");
  draw(
    "Independent is an independent contractor. Nothing in this Agreement establishes an " +
      "employment, agency, or partnership relationship."
  );

  sub("4.2 Taxes (Nigerian Specific).");
  bullet(
    "Withholding Tax: Client shall deduct Withholding Tax (WHT) from payments made to " +
      "the Independent where required by the Companies Income Tax Act (CITA) or Personal " +
      "Income Tax Act (PITA), and remit the same to the relevant Tax Authority."
  );
  bullet(
    "Independent Responsibility: Except for WHT deducted at source, Independent is solely " +
      "responsible for their own income taxes, levies, and statutory contributions (e.g., " +
      "NSITF, Pension). Independent Personnel are not entitled to Client employee benefits."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  5. INTELLECTUAL PROPERTY & OWNERSHIP
  // ════════════════════════════════════════════════════════════════════════════
  section("5", "INTELLECTUAL PROPERTY & OWNERSHIP");

  sub("5.1 Assignment.");
  draw(
    `To the fullest extent permitted by the Nigerian Copyright Act 2022, Independent agrees ` +
      `that all "Independent Work Product" (inventions, designs, code, documents) created for ` +
      `the Client shall be the sole property of the Client.`
  );

  sub("5.2 Transfer of Rights.");
  draw(
    "Independent hereby irrevocably assigns to Client all right, title, and interest in the " +
      "Independent Work Product, including all Intellectual Property Rights."
  );

  sub("5.3 Moral Rights.");
  draw(
    "Independent waives all Moral Rights (right to claim authorship or object to modification) " +
      "in the Work Product to the extent permitted by Nigerian law."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  6. CONFIDENTIALITY
  // ════════════════════════════════════════════════════════════════════════════
  section("6", "CONFIDENTIALITY");
  draw(
    `Independent agrees to hold all "Confidential Information" (Client business data, trade ` +
      `secrets, financial info) in strict confidence and not to disclose it to third parties. ` +
      `This obligation survives the termination of this Agreement.`
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  7. WARRANTIES
  // ════════════════════════════════════════════════════════════════════════════
  section("7", "WARRANTIES");

  sub("7.1 Performance.");
  draw(
    "Independent warrants that Services will be performed in a professional manner " +
      "consistent with industry standards."
  );

  sub("7.2 Non-Infringement.");
  draw(
    "Independent warrants that the Work Product will not infringe on the Intellectual " +
      "Property rights of any third party."
  );

  sub("7.3 Non-Solicitation.");
  draw(
    "For the term of this Agreement and one (1) year thereafter, Independent will not " +
      "directly solicit Client's employees or consultants."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  8. INDEMNITY
  // ════════════════════════════════════════════════════════════════════════════
  section("8", "INDEMNITY");
  draw(
    "Independent will defend and indemnify Client against all claims, losses, and expenses " +
      "arising from: (a) Independent's infringement of third-party IP; (b) negligence " +
      "resulting in injury or property damage; or (c) violation of applicable Nigerian laws."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  9. TERM AND TERMINATION
  // ════════════════════════════════════════════════════════════════════════════
  section("9", "TERM AND TERMINATION");

  sub("9.1 Termination.");
  bullet(`Mutual Consent: Parties may terminate via the "End Project" option on Droomwork.`);
  bullet("Without Cause: Either party may terminate with thirty (30) days' written notice.");
  bullet(
    "For Breach: Either party may terminate for material breach if not cured within ten (10) " +
      "business days, by logging a dispute on Droomwork."
  );

  sub("9.2 Effect.");
  draw(
    "Upon termination, Independent must deliver all Work Product and Confidential " +
      "Information to Client."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  10. DISPUTE RESOLUTION
  // ════════════════════════════════════════════════════════════════════════════
  section("10", "DISPUTE RESOLUTION");

  sub("10.1 Droomwork Assistance.");
  draw(
    "Parties agree to attempt to resolve disputes amicably via the Droomwork platform. " +
      "Submitting a Dispute places a hold on pre-paid funds for up to three (3) months."
  );

  sub("10.2 Governing Law.");
  draw(
    "This Agreement shall be governed by the laws of the Federal Republic of Nigeria."
  );

  sub("10.3 Arbitration.");
  draw(
    "If Droomwork dispute assistance fails to resolve the issue within 14 days, any " +
      "unresolved dispute shall be referred to binding arbitration in accordance with the " +
      "Arbitration and Mediation Act 2023. The seat of arbitration shall be Nigeria. " +
      "Judgment on the award may be entered in any court having jurisdiction."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  11. GENERAL PROVISIONS
  // ════════════════════════════════════════════════════════════════════════════
  section("11", "GENERAL PROVISIONS");
  bullet(
    "Entire Agreement: This Agreement and Droomwork's Terms of Service constitute the " +
      "entire agreement."
  );
  bullet(
    "Electronic Signatures: The Parties consent to use electronic signatures to execute " +
      "this Contract."
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  SIGNATURE BLOCK
  // ════════════════════════════════════════════════════════════════════════════
  gap(24);
  draw("IN WITNESS WHEREOF, the parties have executed this Agreement.", { f: bold });
  gap(28);

  ensureSpace(130);

  const halfW = (contentW - 30) / 2;
  const rightX = MARGIN_X + halfW + 30;
  const sigLine = "______________________________";

  // Column labels
  pg.drawText("INDEPENDENT:", {
    x: MARGIN_X,
    y,
    size: FS.body,
    font: bold,
    color: rgb(0, 0, 0),
  });
  pg.drawText("CLIENT:", {
    x: rightX,
    y,
    size: FS.body,
    font: bold,
    color: rgb(0, 0, 0),
  });
  y -= 30;

  // Signature rows
  const sigRows = [
    { label: "Signature:", value: sigLine },
    { label: "Name:", value: sigLine },
    { label: "Date:", value: sigLine },
  ];

  for (const row of sigRows) {
    ensureSpace(30);
    const labelW = regular.widthOfTextAtSize(row.label, FS.body) + 8;

    // Left column
    pg.drawText(row.label, {
      x: MARGIN_X,
      y,
      size: FS.body,
      font: regular,
      color: rgb(0, 0, 0),
    });
    pg.drawText(row.value, {
      x: MARGIN_X + labelW,
      y,
      size: FS.body,
      font: regular,
      color: rgb(0, 0, 0),
    });

    // Right column
    pg.drawText(row.label, {
      x: rightX,
      y,
      size: FS.body,
      font: regular,
      color: rgb(0, 0, 0),
    });
    pg.drawText(row.value, {
      x: rightX + labelW,
      y,
      size: FS.body,
      font: regular,
      color: rgb(0, 0, 0),
    });

    y -= 30;
  }

  // ── Save & return ──────────────────────────────────────────────────────────
  const pdfBytes = await pdfDoc.save();
  return new File([pdfBytes as Uint8Array<ArrayBuffer>], "droomwork-agreement.pdf", {
    type: "application/pdf",
  });
}
